import { useCallback, useContext, useEffect, useMemo } from 'react';
import clsx from 'clsx';
import { isEqual } from 'lodash-es';
import { LOG_ACTIONS, logAction } from '@namespace/analytics';
import { useGeneralRoutes, useOtherSettings } from '@namespace/cms';
import { ReferenceContext } from '@namespace/common';
import {
  FORM_COMPONENTS_TYPES,
  FORM_VALIDATION_TYPES,
  FormBuilder,
  useFormMethods
} from '@namespace/forms';
import { useI18n } from '@namespace/i18n';
import { Icon } from '@namespace/icons2';
import { useNotifications } from '@namespace/notifications';
import { useHistory } from '@namespace/router';
import { useThemeComponents } from '@namespace/themes';
import { UserContext, useUpdatePhoneNumber2 } from '@namespace/user';
import TextWithDuration from '../../common/HelpText';
import { ERROR_TYPES } from '../../constants';
import usePhoneVerification from '../../hooks/usePhoneVerification';
import { phoneFormField, withPlusPrefix } from '../../utils/phoneFormField';
import useEmailFormComponent from './hooks/useEmailFormComponent';
import LockedText from './LockedText';

import styles from './index.module.css';

const DEFAULT_MASK_CHAR = '_';

const AVAILABLE_VERIFICATION_TYPES = {
  PHONE: 'phone',
  EMAIL: 'email'
};

const PhoneAndEmailVerification = ({
  classNames = {},
  hasTurnOffPhoneVerification = true,
  hidePhoneSubmitButton,
  minNumberLength,
  phoneValidator,
  regPhoneProps = {},
  buttonProps = {},
  hintProps = {},
  disableEditVerifiedPhone = false,
  editButtonProps = {},
  phoneVerificationButtonProps = {},
  textWithDurationProps = {},
  mainInputFieldSettings = {}
}) => {
  const { PhoneAndEmailVerification: PartnerPhoneAndEmailVerification } =
    useThemeComponents();
  const otherSettings = useOtherSettings();
  const {
    availableProfileDataVerificationsTypes = [
      AVAILABLE_VERIFICATION_TYPES.PHONE,
      AVAILABLE_VERIFICATION_TYPES.EMAIL
    ]
  } = otherSettings || {};

  const SHOW_PHONE_VERIFICATION =
    availableProfileDataVerificationsTypes.includes(
      AVAILABLE_VERIFICATION_TYPES.PHONE
    );
  const SHOW_EMAIL_VERIFICATION =
    availableProfileDataVerificationsTypes.includes(
      AVAILABLE_VERIFICATION_TYPES.EMAIL
    );
  const { t } = useI18n();
  const { push } = useHistory();
  const { phoneCodes = [] } = useContext(ReferenceContext);

  const { changePhoneNumber } = useGeneralRoutes();
  const { successNotification, errorNotification } = useNotifications();
  const [user] = useContext(UserContext);
  const { phoneNumber, accountStatuses, email, countryId } = user;
  const { isPhoneVerified } = accountStatuses;
  const { updatePhone, isFetching: isPhoneSaving } = useUpdatePhoneNumber2();
  const phoneVerification = usePhoneVerification();
  const {
    verify,
    confirm,
    cancel,
    verificationData,
    isVerificationTimeout,
    isVerifying,
    isLocked,
    verifyTime,
    lockedTime
  } = phoneVerification;
  const {
    formWrapperClassName = '',
    inputWrapClassName = '',
    buttonClassName = '',
    helpTextClassName = ''
  } = classNames;

  const { element: emailFormComponent } = useEmailFormComponent({
    classNames: {
      inputWrapClassName,
      buttonClassName
    },
    buttonProps,
    hintProps
  });

  const { error = {}, locked = {} } = verificationData;
  const { reason, type: errorType } = error;

  const methods = useFormMethods({
    defaultValues: {
      ...(SHOW_PHONE_VERIFICATION && {
        phone: phoneFormField(phoneCodes, phoneNumber),
        code: ''
      }),
      ...(SHOW_EMAIL_VERIFICATION && {
        email
      })
    }
  });

  const {
    getValues,
    setError,
    formState: { errors },
    clearErrors,
    reset,
    watch,
    setValue
  } = methods;
  const watchPhone = watch('phone');
  const watchCode = watch('code');

  const isPhoneSaved = useMemo(
    () => phoneNumber?.replace('+', '') === watchPhone?.number,
    [phoneNumber, watchPhone]
  );

  useEffect(() => {
    if (reason) {
      setError(
        errorType === ERROR_TYPES.CONFIRM ? 'code' : 'phone',
        'manual',
        /* TODO: findout is there case when a "reason" can be object */
        `accountingMessages.phoneVerification.${reason?.reason || reason}`
      );
    }
  }, [error, setError]);

  useEffect(() => {
    if (SHOW_PHONE_VERIFICATION) {
      const formFieldPhone = phoneFormField(phoneCodes, phoneNumber);

      if (watchPhone && !isEqual(watchPhone, formFieldPhone)) {
        setValue('phone', formFieldPhone);
      }
    }
  }, [phoneNumber, SHOW_PHONE_VERIFICATION]);

  const handleVerify = useCallback(() => {
    clearErrors();
    return verify({ phoneNumber: '' });
  }, [verify, clearErrors]);

  const handleResend = useCallback(() => {
    const { phone } = getValues();

    clearErrors();
    reset({ phone, code: '' });

    return verify({ phoneNumber: '' });
  }, [verify, getValues, clearErrors, reset]);

  const handleSave = useCallback(() => {
    const data = getValues();
    logAction(LOG_ACTIONS.ENTER_CUSTOMER_DATA);
    return updatePhone(withPlusPrefix(data.phone.number))
      .then(() =>
        successNotification(t('phoneVerification.phone.message.saved'))
      )
      .catch(({ errorCode = 'accounting_error_6' }) =>
        errorNotification(t(`accountingMessages.${errorCode}`))
      );
  }, [updatePhone, successNotification, errorNotification, t, getValues]);

  const submit = useCallback(
    (data) => {
      return SHOW_PHONE_VERIFICATION ? confirm({ code: data.code }) : null;
    },
    [confirm, SHOW_PHONE_VERIFICATION]
  );

  const defaultPhoneCode = phoneCodes.find(
    ({ iso1Code }) => iso1Code === countryId
  );

  const dynamicValues = useMemo(
    () => ({
      phone: phoneFormField(
        phoneCodes,
        phoneNumber ||
          (defaultPhoneCode?.phoneCode ? `+${defaultPhoneCode.phoneCode}` : '')
      )
    }),
    [phoneCodes, phoneNumber, defaultPhoneCode]
  );

  const phoneButton = {
    type: FORM_COMPONENTS_TYPES.BUTTON,
    classNames: { className: clsx(styles.phoneButton, buttonClassName) },
    size: 'l',
    intent: 'primary',
    actionType: 'color1',
    ...buttonProps
  };

  const verifyButton = isVerifying
    ? {
        ...phoneButton,
        value: 'phoneVerification.phone.button.edit',
        intent: 'secondary',
        onClick: cancel,
        dataRole: 'phone-verification-btn-edit',
        ...editButtonProps
      }
    : {
        ...phoneButton,
        value: `phoneVerification.phone.button.${
          isPhoneSaved && hasTurnOffPhoneVerification ? 'verify' : 'save'
        }`,
        onClick:
          isPhoneSaved && hasTurnOffPhoneVerification
            ? handleVerify
            : handleSave,
        disabled: isPhoneSaved ? errors.phone || isLocked : errors.phone,
        processing: isPhoneSaving,
        dataRole: `phone-verification-btn-${
          isPhoneSaved && hasTurnOffPhoneVerification ? 'verify' : 'save'
        }`
      };
  const phoneInputGroup = {
    type: FORM_COMPONENTS_TYPES.FORM_ROW,
    name: 'phoneVerification',
    label: 'phoneVerification.phone.label',
    fields: [
      {
        type: FORM_COMPONENTS_TYPES.REG_PHONE,
        name: 'phone',
        validationTypes: phoneValidator
          ? [phoneValidator]
          : [
              FORM_VALIDATION_TYPES.REQUIRED,
              FORM_VALIDATION_TYPES.PHONE_ONLY_NUMBER
            ],
        minNumberLength,
        disabled:
          isPhoneVerified || isVerifying || isLocked || hidePhoneSubmitButton,
        formProps: { dynamicValues },
        inputSize: 's',
        intent: 'default',
        dataRole: 'phone-verification',
        classNames: { containerClassName: styles.phoneInput, buttonClassName },
        ...regPhoneProps,
        ...(isPhoneVerified &&
          !hidePhoneSubmitButton &&
          !disableEditVerifiedPhone && {
            icon: (
              <Icon
                onClick={() => push(changePhoneNumber)}
                className={styles.changePhoneIcon}
                name="icons/general/action/edit"
                color="input_txt"
                size="s"
              />
            )
          })
      },
      !isPhoneVerified && !hidePhoneSubmitButton && verifyButton
    ],
    classNames: { labelClassName: styles.label },
    dataRole: 'phone-verification'
  };

  const {
    iconNameStateVerified = 'icons/general/state/success',
    iconNameStateNotVerified = 'icons/general/state/warning',
    iconColorStateNotVerified = 'state_warning',
    iconColorStateVerified = 'state_success',
    iconClassName = '',
    textColor = 'po_txt_color_3',
    hintClassName = ''
  } = hintProps;
  const phoneHelpText = {
    type: FORM_COMPONENTS_TYPES.FORM_HELP_TEXT,
    children: t(
      `phoneVerification.phone.message.${isPhoneVerified ? '' : 'not'}verified`
    ),
    icon: isPhoneVerified ? iconNameStateVerified : iconNameStateNotVerified,
    iconColor: isPhoneVerified
      ? iconColorStateVerified
      : iconColorStateNotVerified,
    className: clsx(styles.helpText, hintClassName),
    iconClassName,
    textColor
  };

  const button = isVerificationTimeout
    ? {
        type: FORM_COMPONENTS_TYPES.BUTTON,
        value: 'phoneVerification.code.button.resend',
        disabled: isLocked,
        onClick: handleResend,
        classNames: { className: styles.button },
        dataRole: 'phone-verification-btn-resend',
        ...phoneVerificationButtonProps
      }
    : {
        type: FORM_COMPONENTS_TYPES.SUBMIT_BUTTON,
        value: 'phoneVerification.code.button.submit',
        disabled:
          watchCode?.replaceAll(DEFAULT_MASK_CHAR, '').length < 3 || isLocked,
        classNames: { className: styles.button },
        dataRole: 'phone-verification-btn-submit',
        ...phoneVerificationButtonProps
      };

  const codeInputField = {
    type: FORM_COMPONENTS_TYPES.INPUT_FIELD,
    label: 'phoneVerification.code.label',
    mask: '99999',
    name: 'code',
    disabled: isVerificationTimeout || isLocked,
    validationTypes: [
      FORM_VALIDATION_TYPES.REQUIRED,
      {
        type: FORM_VALIDATION_TYPES.CODE_VALIDATOR,
        parameters: [undefined, DEFAULT_MASK_CHAR]
      }
    ],
    classNames: {
      wrapperClassName: styles.codeInputWrap,
      inputClassName: styles.input
    },
    isOnChangeValidation: false,
    isClearErrorsOnChange: true,
    dataRole: 'phone-verification-code'
  };

  const codeHelpText = {
    type: FORM_COMPONENTS_TYPES.FORM_HELP_TEXT,
    className: clsx(styles.helpText, helpTextClassName),
    children: (
      <TextWithDuration
        text={t('phoneVerification.code.hint.time')}
        time={verifyTime}
        textWithDurationProps={textWithDurationProps}
      />
    )
  };

  const lockedText = {
    type: FORM_COMPONENTS_TYPES.FORM_HELP_TEXT,
    className: clsx(styles.helpText, helpTextClassName),
    children: (
      <LockedText
        textWithDurationProps={textWithDurationProps}
        time={lockedTime}
        text={t(
          `accountingMessages.phoneVerification.${
            locked.reason?.reason || locked.reason
          }`
        )}
      />
    )
  };

  const codeForm = [
    ...((SHOW_PHONE_VERIFICATION && [
      codeInputField,
      Boolean(lockedTime) && lockedText,
      Boolean(verifyTime) && !lockedTime && codeHelpText,
      button
    ]) ||
      [])
  ];

  const unVerifyingComponentList = [
    ...((SHOW_EMAIL_VERIFICATION && [emailFormComponent]) || []),
    ...((SHOW_PHONE_VERIFICATION && [phoneInputGroup]) || [])
  ];

  if (
    hasTurnOffPhoneVerification &&
    SHOW_PHONE_VERIFICATION &&
    !hidePhoneSubmitButton
  ) {
    unVerifyingComponentList.push(lockedTime ? lockedText : phoneHelpText);
  }
  const componentsList = isVerifying
    ? [
        ...((SHOW_EMAIL_VERIFICATION && [emailFormComponent]) || []),
        ...((SHOW_PHONE_VERIFICATION && [phoneInputGroup]) || []),
        ...codeForm
      ]
    : unVerifyingComponentList;

  if (PartnerPhoneAndEmailVerification) {
    return (
      <PartnerPhoneAndEmailVerification
        hasTurnOffPhoneVerification={hasTurnOffPhoneVerification}
        hidePhoneSubmitButton={hidePhoneSubmitButton}
        minNumberLength={minNumberLength}
        phoneValidator={phoneValidator}
        mainInputFieldSettings={mainInputFieldSettings}
        regPhoneProps={regPhoneProps}
        handleResend={handleResend}
        dynamicValues={dynamicValues}
        submit={submit}
        lockedText={lockedText}
        methods={methods}
        phoneButton={phoneButton}
        isPhoneVerified={isPhoneVerified}
        emailFormComponent={emailFormComponent}
        phoneVerification={phoneVerification}
        verifyButton={verifyButton}
      />
    );
  }

  return (
    <FormBuilder
      components={componentsList}
      formMethods={methods}
      onSubmit={submit}
      wrapperClassName={clsx([formWrapperClassName, styles.formWrapp])}
      formProps={{
        loadingOnDisabled: false,
        isLoading: false
      }}
    />
  );
};

export default PhoneAndEmailVerification;
